import { useEffect, useState } from "react";
import { useLDClient } from "launchdarkly-react-client-sdk";
import {
    getLDUserKey,
    getLDWorkspaceKey,
    getLDOrganizationKey,
} from "@common/helpers/featureFlags";
import { getPossessive } from "@common/helpers/string";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { UserSchema } from "@common/schemas/user.schema";
import { useUser } from "@hooks/useUser";
import { identifyUser } from "@lib/analytics";
import { getEnv } from "@lib/environment";
import { identify } from "@lib/hubspot";
import type { FeatureFlagsIdentity } from "./featureFlagsIdentity";

const getCypressOrRealUser = (user: UserSchema, kickEnv: string) => {
    if (Object.hasOwn(window, "Cypress")) {
        return "e2e-test-user";
    }
    return getLDUserKey(kickEnv, user.id);
};

const getCypressOrRealWorkspace = (workspaceId: string, kickEnv: string) => {
    if (Object.hasOwn(window, "Cypress")) {
        return "e2e-test-workspace";
    }
    return getLDWorkspaceKey(kickEnv, workspaceId);
};

const getCypressOrRealOrganization = (
    organizationId: string,
    kickEnv: string,
) => {
    if (Object.hasOwn(window, "Cypress")) {
        return "e2e-test-organization";
    }
    return getLDOrganizationKey(kickEnv, organizationId);
};

/** Identifies the user to analytics and LaunchDarkly; returns who LaunchDarkly finished identifying. */
export const useIdentification = (): FeatureFlagsIdentity | null => {
    const user = useUser();
    const activeWorkspace = useActiveWorkspace();
    const activeOrganization = useActiveOrganization();
    const ldClient = useLDClient();
    const kickEnv = getEnv("KICK_ENV");
    const isDemo = activeWorkspace?.isDemo;
    const [featureFlagsIdentity, setFeatureFlagsIdentity] =
        useState<FeatureFlagsIdentity | null>(null);

    useEffect(() => {
        if (!user) {
            return;
        }

        identifyUser(user);
        identify(user);

        if (!ldClient) {
            return;
        }

        let isCurrent = true;
        const identity: FeatureFlagsIdentity = {
            userId: user.id,
            organizationId: activeOrganization?.id ?? null,
        };
        const markIdentified = () => {
            if (isCurrent) {
                setFeatureFlagsIdentity(identity);
            }
        };

        setFeatureFlagsIdentity(null);
        ldClient
            .identify({
                kind: "multi",
                user: {
                    key: getCypressOrRealUser(user, kickEnv),
                    name: user.name,
                    email: user.email,
                    custom: {
                        createdAt: user.createdAt
                            ? new Date(user.createdAt).getTime()
                            : null,
                    },
                },
                ...(activeWorkspace?.id
                    ? {
                          workspace: {
                              key: getCypressOrRealWorkspace(
                                  activeWorkspace.id,
                                  kickEnv,
                              ),
                              name: `${getPossessive(activeWorkspace.name)} Workspace`,
                              custom: {
                                  isDemo,
                                  plan: activeWorkspace.plan,
                                  createdAt:
                                      activeWorkspace.createdAt.getTime(),
                                  tempMigratedToNormalizedDescriptions:
                                      activeWorkspace.tempMigratedToNormalizedDescriptions,
                                  tempMigratedToReasoningCategorization:
                                      activeWorkspace.tempMigratedToReasoningCategorization,
                                  tempMigratedToSimilarityRules:
                                      activeWorkspace.tempMigratedToSimilarityRules,
                              },
                          },
                      }
                    : {}),
                ...(activeOrganization?.id
                    ? {
                          organization: {
                              key: getCypressOrRealOrganization(
                                  activeOrganization.id,
                                  kickEnv,
                              ),
                              name: `${getPossessive(activeOrganization.name)} Organization`,
                              custom: {
                                  createdAt: activeOrganization.createdAt
                                      ? new Date(
                                            activeOrganization.createdAt,
                                        ).getTime()
                                      : null,
                              },
                          },
                      }
                    : {}),
            })
            // A failed identify keeps the flags it had; waiting longer won't change them.
            .then(markIdentified, markIdentified);

        return () => {
            isCurrent = false;
        };
    }, [user, ldClient, kickEnv, activeWorkspace, isDemo, activeOrganization]);

    return featureFlagsIdentity;
};
